import type { ColumnDef } from "@tanstack/react-table";
import { Info } from "lucide-react";

import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { TruncatedText } from "@krea/shared/design-system/TruncatedText";

import { LenderUserRoleEnum } from "@/features/common/enums";
import type { ILenderUser } from "@/features/users/types";

import { TeamMemberActions } from "./team-member-actions";
import { TeamMemberRoleCell } from "./team-member-role-cell";

// KREA-4497. The five text columns declare a PERCENTAGE (`meta.stretch`, summing to 100) rather
// than a pixel `size`, so the table always fills its card and they share the space proportionally
// as the viewport changes; the actions column is the one exception and is explained at its own
// definition. With the previous pixel sizes each column was frozen at its declared width —
// originally TanStack's 150px default for all six, which is narrower than a real email address,
// and since an email has no spaces to wrap at it overflowed its cell and painted over the phone
// column next to it.
//
// Percentages mean a column can still end up narrower than its content, so the text cells clip with
// an ellipsis and reveal the full value on hover/tap via `TruncatedText` instead of overflowing.
export const columns: ColumnDef<ILenderUser>[] = [
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    meta: { stretch: 19 },
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => {
      const m = row.original;

      return (
        <TruncatedText className="text-base-strong">
          {m.firstName} {m.lastName}
        </TruncatedText>
      );
    },
  },
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    meta: { stretch: 27 },
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => (
      <TruncatedText className="text-base">
        {row.original.email ?? "—"}
      </TruncatedText>
    ),
  },
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    meta: { stretch: 17 },
    accessorKey: "mobilePhoneNumber",
    header: "Phone",
    cell: ({ row }) => (
      <TruncatedText className="text-base">
        {row.original.mobilePhoneNumber ?? "—"}
      </TruncatedText>
    ),
  },
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    meta: { stretch: 15 },
    accessorKey: "ssn",
    header: "SSN",
    cell: ({ row }) => (
      <TruncatedText className="text-base">
        {/* hide last 4 digits */}
        {row.original.ssn ? `${row.original.ssn.slice(0, -4)}****` : "N/A"}
      </TruncatedText>
    ),
  },

  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    meta: { stretch: 22 },
    accessorKey: "role",
    header: () => (
      <div className="flex items-center gap-2">
        <span>Role</span>
        <ResponsiveTooltip trigger={<Info className="size-4" />}>
          <div>The role of the user in the lender company.</div>
          <strong>Roles:</strong>
          <ul className="list-inside list-disc">
            <li>
              <strong>{LenderUserRoleEnum.CREDIT_OFFICER}:</strong>
              <ul className="ml-4 list-inside list-disc">
                <li>
                  The user can view applications and perform actions on them.
                </li>
              </ul>
            </li>
            <li>
              <strong>{LenderUserRoleEnum.DEVELOPER}:</strong>
              <ul className="ml-4 list-inside list-disc">
                <li>
                  Everything a user with the{" "}
                  <strong>{LenderUserRoleEnum.CREDIT_OFFICER}</strong> role can
                  do.
                </li>
                <li>
                  The user can view and edit the API configuration and
                  integration logs.
                </li>
              </ul>
            </li>
            <li>
              <strong>{LenderUserRoleEnum.ADMIN}:</strong>
              <ul className="ml-4 list-inside list-disc">
                <li>
                  Everything a user with the{" "}
                  <strong>{LenderUserRoleEnum.DEVELOPER}</strong> role can do.
                </li>
                <li>
                  The user has all permissions in the lender company. The user
                  can view and edit the lender company and settings.
                </li>
                <li>
                  The user can view the team members, invite new users, remove
                  users and edit their roles and basic information.
                </li>
              </ul>
            </li>
          </ul>
        </ResponsiveTooltip>
      </div>
    ),
    cell: ({ row }) => <TeamMemberRoleCell user={row.original} />,
  },
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    // The one column that is NOT fluid. Its two icon buttons carry `shrink-0`, so the content has
    // an irreducible 84px (2x32 + `gap-1` + the cell's `px-2`) and a percentage would let the cell
    // fall below it and spill into Role — 8% only clears 84px on a table wider than ~1050px. A
    // per-cell `min-width` cannot rescue that: under `table-fixed` it is ignored, since the table
    // may not exceed its container. A fixed `size` is honoured at every width instead, and the
    // percentages below sum to 100 across the remaining space.
    size: 84,
    id: "actions",
    header: () => null,
    cell: ({ row }) => {
      const m = row.original;

      return <TeamMemberActions user={m} />;
    },
  },
];
